/// <reference lib="dom" />
import type { ParamDict } from "@utils/params";
import {
  BitcrusherEffectDef,
  ChorusEffectDef,
  CompressorEffectDef,
  Cowbell808Synth,
  DelayEffectDef,
  FilterEffectDef,
  GainEffectDef,
  HiHat808Synth,
  JazzBrushSweepSynth,
  JazzClaveSynth,
  JazzGuiroSynth,
  JazzHiHatSynth,
  JazzKickSynth,
  JazzRideSynth,
  JazzRimshotSynth,
  JazzShakerSynth,
  JazzSnareSynth,
  MoogFilterEffectDef,
  OceanSynth,
  OpenHiHat808Synth,
  OverdriveCurve,
  OverdriveEffectDef,
  PannerEffectDef,
  PhaserEffectDef,
  PingPongDelayEffectDef,
  Rimshot808Synth,
  ShushSynth,
  TelephonizerEffectDef,
  TremoloEffectDef,
  WahWahEffectDef,
  WindSynth,
} from "@wbaudio/core/synth";
import {
  AdsrGraph,
  CompressorGraph,
  CrushGraph,
  DelayGraph,
  FilterGraph,
  GainGraph,
  LfoGraph,
  MoogFilterGraph,
  PanGraph,
  type ParamGraph,
  PhaserGraph,
  PingPongGraph,
  TelephonizerGraph,
  TremoloGraph,
  WahWahGraph,
  WaveshaperGraph,
} from "./paramGraphs.tsx";

function OverdriveTransfer(params: ParamDict) {
  const table = OverdriveCurve(
    params.algorithmIndex.value as number,
    params.curveAmount.value as number,
  );
  const drive = params.drive.value as number;
  const last = table.length - 1;
  return (input: number) => {
    const x = Math.max(-1, Math.min(1, input * drive));
    return table[Math.round(((x + 1) / 2) * last)];
  };
}

const HatGraphs = [
  FilterGraph("bandpass", {
    frequency: "bandpass/frequency",
    q: "bandpass/q",
    filterType: "bandpass",
  }),
  FilterGraph("highpass", { frequency: "highpass/frequency", filterType: "highpass" }),
];

const MetalGraphs = [
  FilterGraph("bandpass", {
    frequency: "bandpass/frequency",
    q: "bandpass/q",
    filterType: "bandpass",
  }),
  FilterGraph("highpass", {
    frequency: "hipass/frequency",
    q: "hipass/q",
    filterType: "highpass",
  }),
];

const BandpassGraph = FilterGraph("filter", {
  frequency: "filter/frequency",
  q: "filter/q",
  filterType: "bandpass",
});

const OceanStage = (stage: string) =>
  FilterGraph(stage, {
    frequency: `${stage}/frequency`,
    q: `${stage}/q`,
    type: `${stage}/type`,
    extra: [`${stage}/ramp`, `${stage}/duration`],
  });

export const DefGraphs = new Map<object, ParamGraph[]>([
  [HiHat808Synth, HatGraphs],
  [OpenHiHat808Synth, HatGraphs],
  [Cowbell808Synth, [BandpassGraph]],
  [
    Rimshot808Synth,
    [FilterGraph("noise", { frequency: "noise/highpass", filterType: "highpass" })],
  ],
  [
    JazzKickSynth,
    [FilterGraph("highpass", {
      frequency: "hipass/frequency",
      q: "hipass/q",
      filterType: "highpass",
    })],
  ],
  [
    JazzSnareSynth,
    [FilterGraph("noise", {
      frequency: "noiseFilter/frequency",
      q: "noiseFilter/q",
      filterType: "lowpass",
    })],
  ],
  [JazzRideSynth, MetalGraphs],
  [JazzHiHatSynth, MetalGraphs],
  [
    JazzRimshotSynth,
    [FilterGraph("noise", {
      frequency: "noiseFilter/frequency",
      q: "noiseFilter/q",
      filterType: "highpass",
    })],
  ],
  [
    JazzBrushSweepSynth,
    [FilterGraph("sweep", {
      frequency: "filter/frequency",
      q: "filter/q",
      filterType: "bandpass",
      extra: ["filter/ramp"],
    })],
  ],
  [JazzClaveSynth, [BandpassGraph]],
  [JazzShakerSynth, [BandpassGraph]],
  [JazzGuiroSynth, [BandpassGraph]],
  [
    WindSynth,
    [FilterGraph("filter", {
      frequency: "filter/startFreq",
      q: "filter/q",
      type: "filter/type",
    })],
  ],
  [
    ShushSynth,
    [
      AdsrGraph("envelope", "envelope/"),
      FilterGraph("filter", {
        frequency: "filter/frequency",
        q: "filter/q",
        type: "filter/type",
        extra: ["filter/ramp", "filter/duration"],
      }),
      FilterGraph("lowpass", {
        frequency: "lowpass/frequency",
        q: "lowpass/q",
        type: "lowpass/type",
        extra: ["lowpass/ramp", "lowpass/duration"],
      }),
    ],
  ],
  [OceanSynth, ["highshelf", "lowshelf", "highpass", "lowpass"].map(OceanStage)],
  [
    FilterEffectDef,
    [FilterGraph("response", {
      frequency: "frequency",
      q: "q",
      type: "filterType",
      extra: ["gain"],
    })],
  ],
  [CompressorEffectDef, [CompressorGraph()]],
  [OverdriveEffectDef, [WaveshaperGraph(OverdriveTransfer)]],
  [TelephonizerEffectDef, [TelephonizerGraph()]],
  [TremoloEffectDef, [TremoloGraph()]],
  [ChorusEffectDef, [LfoGraph()]],
  [PhaserEffectDef, [PhaserGraph(), LfoGraph()]],
  [MoogFilterEffectDef, [MoogFilterGraph()]],
  [WahWahEffectDef, [WahWahGraph()]],
  [DelayEffectDef, [DelayGraph()]],
  [PingPongDelayEffectDef, [PingPongGraph()]],
  [BitcrusherEffectDef, [CrushGraph()]],
  [GainEffectDef, [GainGraph()]],
  [PannerEffectDef, [PanGraph()]],
]);

export function GraphsOf(def: object): ParamGraph[] {
  return DefGraphs.get(def) ?? [];
}
